import { CHANNEL_LABELS, CHANNEL_COLORS, CHANNEL_OPTIONS, CAMPAIGN_STATUSES, CHANNEL_OPTIONS_EXTERIOR, CAMPAIGN_TYPES } from '@/utils/campaignOptions'
import campaignsService from '@/api/campaignsService'
import moment from 'moment'
import authService, { USER_ROLES } from '@/api/authService'

const channelMock = {
  comment: '',
  selected_format: '',
  start_date: '',
  finish_date: '',
  campaigns: 0,
  budget: 0,
  zones: []
}

const channelExteriorOptions = Object.keys(CHANNEL_OPTIONS_EXTERIOR).map(channelFromType)
const channelDigitalOptions = [
  CHANNEL_OPTIONS.LOCALINVENTORYADS,
  CHANNEL_OPTIONS.GOOGLESEARCHADS,
  CHANNEL_OPTIONS.DV360,
  CHANNEL_OPTIONS.META,
  CHANNEL_OPTIONS.GEOLOCATION
].map(channelFromType)
// const channelDigitalOptions = Object.keys(CHANNEL_OPTIONS).filter(c => c !== 'INSTAGRAM').map(channelFromType)

function channelFromType (key) {
  const title = CHANNEL_LABELS[key]
  const color = CHANNEL_COLORS[key]
  return { ...channelMock, type: key, title, color }
}

export default {
  name: 'ActivationChannels',
  mixins: [],
  components: {
    ChannelCard: () => import(/* webpackChunkName: "ChannelCard" */ '@/components/channel-card/ChannelCard.vue')
  },
  props: {
    campaign: {
      type: Object,
      required: true,
      default: () => ({
        channels: []
      })
    },
    readonly: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  data () {
    return {
      isDeleting: false,
      isSending: false,
      readyError: false,
      authData: authService.authData
    }
  },
  computed: {
    isExterior () {
      return this.campaign && this.campaign.type === CAMPAIGN_TYPES.EXTERIOR
    },
    linkFirstPart () {
      return this.type === CAMPAIGN_TYPES.EXTERIOR ? '/exterior-campaigns' : '/campaigns'
    },
    linkGenericPart () {
      return '/generic-channels'
    },
    showSelector () {
      if (this.readonly) {
        return false
      }
      return this.type === CAMPAIGN_TYPES.EXTERIOR
        ? this.campaign.channels.length === 0
        : true
    },
    type () {
      return this.campaign.type
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    channelOptions () {
      const digitalOptions = channelDigitalOptions.filter(c => c.type !== 'YOUTUBE')

      if (this.isDirector) {
        return this.type === CAMPAIGN_TYPES.EXTERIOR ? channelExteriorOptions : digitalOptions
      } else {
        return this.type === CAMPAIGN_TYPES.EXTERIOR ? channelExteriorOptions : channelDigitalOptions
      }
    },
    currentChannels () {
      if (this.isDirector) {
        return this.campaign.channels.filter(c => c.type !== 'YOUTUBE')
      } else {
        return this.campaign.channels
      }
    },
    isCompleted () {
      return !!(this.startDateValid && this.campaign.channels.filter(c => c.id).length)
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.ROOT ||
        this.authData.user.role === USER_ROLES.CENTRAL_ADMIN
    },
    startDateRequiredDiff () {
      return this.isAdmin ? 0 : 4
    },
    startDateValid () {
      return moment(this.campaign.start_date).diff(moment().startOf('day'), 'days') > this.startDateRequiredDiff
    },
    channelDatesHaveError () {
      return this.campaign.channels.some(c => c.errors && c.errors.length)
    },
    hasValidStatus () {
      const statuses = [CAMPAIGN_STATUSES.DRAFT]
      return this.campaign && statuses.indexOf(this.campaign.status) > -1
    }
  },
  beforeDestroy () {
    this.campaign.channels = this.campaign.channels.filter(c => c.id)
  },
  methods: {
    isChannelSelected (opt) {
      return this.campaign.channels.some(c => c.type === opt.type)
    },
    onSwitchChange ({ name: type, state }) {
      if (state) {
        this.campaign.channels.push(channelFromType(type))
      } else {
        this.campaign.channels = this.campaign.channels.filter(c => c.type !== type)
      }
      this.updateScrollbar()
    },
    updateScrollbar () {
      window.setTimeout(() => {
        if (this.$refs.scrollbar) {
          this.$refs.scrollbar.update()
        }
      }, 300)
    },
    back () {
      this.$emit('back')
    },
    async send () {
      const prevStatus = this.campaign.status
      const draftStatus = CAMPAIGN_STATUSES.DRAFT
      const readyStatus = this.isExterior ? CAMPAIGN_STATUSES.AGENCY_PROVISIONING : CAMPAIGN_STATUSES.CENTRAL_READY

      let nextStatus = prevStatus === draftStatus
        ? readyStatus
        : draftStatus

      try {
        if (prevStatus !== readyStatus) {
          await campaignsService.updateStatus({
            id: this.campaign.id,
            status: nextStatus
          })
          this.campaign.status = nextStatus
        }
        this.$emit('next')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[activation-channels.js] Error updating')
        this.readyError = true
      }
    },
    onChannelsChange () {
      this.$emit('channelsChange')
    },
    async deleteCampaign () {
      this.isDeleting = true
      try {
        await campaignsService.deleteCampaign({ id: this.campaign.id })
        this.$emit('delete', this.campaign.id)
        this.$router.push('/campaigns')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[activation-channels.js] Error deleting campaign', err)
      }
      this.isDeleting = false
    }
  }
}
